import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { userApi, cvApi, integrationsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    User, Github, Linkedin, Mail, FileText, Save, 
    ArrowLeft, Loader2, Trash2, ExternalLink, Shield, AlertTriangle,
    Wallet, Star, GitFork, Code, Send
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function ProfilePage() {
    const { user, setUser, logout } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState({
        github_url: '',
        linkedin_url: '',
        telegram_handle: '',
        bio: '',
        skills: [],
        wallet_address: '',
        github_username: ''
    });
    const [cvs, setCvs] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [newSkill, setNewSkill] = useState('');
    const [githubStats, setGithubStats] = useState(null);
    const [etherscanStats, setEtherscanStats] = useState(null);
    const [isLoadingGithub, setIsLoadingGithub] = useState(false);
    const [isLoadingWallet, setIsLoadingWallet] = useState(false);

    useEffect(() => {
        loadProfile();
        loadCvs();
    }, []);

    const loadProfile = async () => {
        try {
            const data = await userApi.getProfile();
            setProfile({
                github_url: data.github_url || '',
                linkedin_url: data.linkedin_url || '',
                telegram_handle: data.telegram_handle || '',
                bio: data.bio || '',
                skills: data.skills || [],
                wallet_address: data.wallet_address || '',
                github_username: data.github_username || ''
            });
            
            // Load GitHub stats if username exists
            if (data.github_username) {
                loadGithubStats(data.github_username);
            }
            
            // Load Etherscan stats if wallet exists
            if (data.wallet_address) {
                loadEtherscanStats(data.wallet_address);
            }
        } catch (error) {
            console.error('Error loading profile:', error);
        }
    };

    const loadGithubStats = async (username) => {
        setIsLoadingGithub(true);
        try {
            const stats = await integrationsApi.getGitHubStats(username);
            if (!stats.error) {
                setGithubStats(stats);
            }
        } catch (error) {
            console.error('Error loading GitHub stats:', error);
        } finally {
            setIsLoadingGithub(false);
        }
    };

    const loadEtherscanStats = async (address) => {
        setIsLoadingWallet(true);
        try {
            const stats = await integrationsApi.getEtherscanStats(address);
            if (!stats.error) {
                setEtherscanStats(stats);
            }
        } catch (error) {
            console.error('Error loading Etherscan stats:', error);
        } finally {
            setIsLoadingWallet(false);
        }
    };

    const handleLinkGithub = async () => {
        const username = profile.github_url?.split('github.com/')[1]?.split('/')[0] || '';
        if (!username) {
            toast.error('Please enter a valid GitHub URL first');
            return;
        }
        
        setIsLoadingGithub(true);
        try {
            await integrationsApi.linkGitHub(username);
            setProfile(prev => ({ ...prev, github_username: username }));
            await loadGithubStats(username);
            toast.success('GitHub account linked!');
        } catch (error) {
            toast.error(error.message || 'Failed to link GitHub');
        } finally {
            setIsLoadingGithub(false);
        }
    };

    const handleLinkWallet = async () => {
        if (!profile.wallet_address || !profile.wallet_address.startsWith('0x')) {
            toast.error('Please enter a valid Ethereum address');
            return;
        }
        
        setIsLoadingWallet(true);
        try {
            await integrationsApi.linkWallet(profile.wallet_address);
            await loadEtherscanStats(profile.wallet_address);
            toast.success('Wallet linked!');
        } catch (error) {
            toast.error(error.message || 'Failed to link wallet');
        } finally {
            setIsLoadingWallet(false);
        }
    };

    const loadCvs = async () => {
        try {
            const data = await cvApi.list();
            setCvs(data);
        } catch (error) {
            console.error('Error loading CVs:', error);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updated = await userApi.updateProfile(profile);
            setUser(prev => ({ ...prev, ...updated }));
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddSkill = () => {
        if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
            setProfile(prev => ({
                ...prev,
                skills: [...prev.skills, newSkill.trim()]
            }));
            setNewSkill('');
        }
    };

    const handleRemoveSkill = (skillToRemove) => {
        setProfile(prev => ({
            ...prev,
            skills: prev.skills.filter(s => s !== skillToRemove)
        }));
    };

    const handleDeleteCv = async (cvId) => {
        try {
            await cvApi.delete(cvId);
            setCvs(prev => prev.filter(cv => cv.cv_id !== cvId));
            toast.success('CV deleted');
        } catch (error) {
            toast.error('Failed to delete CV');
        }
    };

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteAllData = async () => {
        setIsDeleting(true);
        try {
            await userApi.deleteAllData();
            toast.success('All your data has been deleted');
            logout();
            navigate('/');
        } catch (error) {
            toast.error('Failed to delete data');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <div className="min-h-screen bg-void page-enter">
            <div className="noise-overlay" />

            {/* Header */}
            <header className="sticky top-0 z-20 glass border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link 
                        to="/dashboard" 
                        className="text-muted-foreground hover:text-acid transition-colors"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                    </Link>
                    <h1 className="font-display text-xl font-bold text-white">Profile</h1>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-8">
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left Column - User Info */}
                    <div className="lg:col-span-1 space-y-6">
                        <Card className="bg-void-paper border-border">
                            <CardContent className="p-6 text-center">
                                <Avatar className="w-24 h-24 mx-auto mb-4 border-2 border-acid">
                                    <AvatarImage src={user?.picture} />
                                    <AvatarFallback className="bg-acid text-black font-display text-2xl">
                                        {user?.name?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <h2 className="font-display text-xl font-bold text-white mb-1">
                                    {user?.name}
                                </h2>
                                <p className="text-muted-foreground text-sm font-mono flex items-center justify-center gap-2">
                                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                                    {user?.email}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Quick Links */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-sm">Quick Links</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {profile.github_url && (
                                    <a
                                        href={profile.github_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-sm border border-border hover:border-acid/50 transition-colors group"
                                    >
                                        <Github className="w-5 h-5 text-muted-foreground group-hover:text-acid" strokeWidth={1.5} />
                                        <span className="text-sm text-muted-foreground group-hover:text-white">GitHub</span>
                                        <ExternalLink className="w-4 h-4 text-muted-foreground ml-auto" strokeWidth={1.5} />
                                    </a>
                                )}
                                {profile.linkedin_url && (
                                    <a
                                        href={profile.linkedin_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-sm border border-border hover:border-acid/50 transition-colors group"
                                    >
                                        <Linkedin className="w-5 h-5 text-muted-foreground group-hover:text-acid" strokeWidth={1.5} />
                                        <span className="text-sm text-muted-foreground group-hover:text-white">LinkedIn</span>
                                        <ExternalLink className="w-4 h-4 text-muted-foreground ml-auto" strokeWidth={1.5} />
                                    </a>
                                )}
                                {profile.telegram_handle && (
                                    <a
                                        href={`https://t.me/${profile.telegram_handle}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-sm border border-border hover:border-[#0088cc]/50 transition-colors group"
                                    >
                                        <Send className="w-5 h-5 text-muted-foreground group-hover:text-[#0088cc]" strokeWidth={1.5} />
                                        <span className="text-sm text-muted-foreground group-hover:text-white">@{profile.telegram_handle}</span>
                                        <ExternalLink className="w-4 h-4 text-muted-foreground ml-auto" strokeWidth={1.5} />
                                    </a>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - Edit Form */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <User className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                    Edit Profile
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider">
                                        GitHub URL
                                    </label>
                                    <Input
                                        value={profile.github_url}
                                        onChange={(e) => setProfile(prev => ({ ...prev, github_url: e.target.value }))}
                                        placeholder="https://github.com/username"
                                        className="bg-black/20 border-border focus:border-acid font-mono text-sm"
                                        data-testid="github-input"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider">
                                        LinkedIn URL
                                    </label>
                                    <Input
                                        value={profile.linkedin_url}
                                        onChange={(e) => setProfile(prev => ({ ...prev, linkedin_url: e.target.value }))}
                                        placeholder="https://linkedin.com/in/username"
                                        className="bg-black/20 border-border focus:border-acid font-mono text-sm"
                                        data-testid="linkedin-input"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider flex items-center gap-2">
                                        <Send className="w-4 h-4 text-[#0088cc]" />
                                        Telegram Handle
                                    </label>
                                    <Input
                                        value={profile.telegram_handle}
                                        onChange={(e) => setProfile(prev => ({ ...prev, telegram_handle: e.target.value.replace('@', '') }))}
                                        placeholder="username (without @)"
                                        className="bg-black/20 border-border focus:border-[#0088cc] font-mono text-sm"
                                        data-testid="telegram-input"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Allow employers to reach you directly on Telegram
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider">
                                        Bio
                                    </label>
                                    <Textarea
                                        value={profile.bio}
                                        onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                                        placeholder="Tell us about yourself..."
                                        rows={4}
                                        className="bg-black/20 border-border focus:border-acid font-sans text-sm resize-none"
                                        data-testid="bio-input"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider">
                                        Skills
                                    </label>
                                    <div className="flex gap-2 mb-3">
                                        <Input
                                            value={newSkill}
                                            onChange={(e) => setNewSkill(e.target.value)}
                                            placeholder="Add a skill..."
                                            className="bg-black/20 border-border focus:border-acid font-mono text-sm"
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddSkill()}
                                            data-testid="skill-input"
                                        />
                                        <Button
                                            onClick={handleAddSkill}
                                            variant="outline"
                                            className="border-border hover:border-acid hover:text-acid font-mono text-xs uppercase"
                                            data-testid="add-skill-btn"
                                        >
                                            Add
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {profile.skills.map((skill, idx) => (
                                            <Badge
                                                key={idx}
                                                variant="outline"
                                                className="font-mono text-xs border-acid/30 text-acid bg-acid/5 cursor-pointer hover:bg-neon-red/10 hover:border-neon-red/30 hover:text-neon-red transition-colors"
                                                onClick={() => handleRemoveSkill(skill)}
                                            >
                                                {skill} ×
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* Web3 Credentials Section */}
                                <div className="pt-4 mt-4 border-t border-border">
                                    <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4">
                                        Web3 Credentials (Optional)
                                    </h3>
                                    
                                    {/* GitHub Verification */}
                                    <div className="mb-4">
                                        <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider flex items-center gap-2">
                                            <Github className="w-4 h-4" />
                                            Verify GitHub Profile
                                        </label>
                                        <div className="flex gap-2">
                                            <Input
                                                value={profile.github_url}
                                                onChange={(e) => setProfile(prev => ({ ...prev, github_url: e.target.value }))}
                                                placeholder="https://github.com/username"
                                                className="bg-black/20 border-border focus:border-acid font-mono text-sm"
                                            />
                                            <Button
                                                onClick={handleLinkGithub}
                                                disabled={isLoadingGithub}
                                                variant="outline"
                                                className="border-acid text-acid hover:bg-acid/10"
                                            >
                                                {isLoadingGithub ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    'Verify'
                                                )}
                                            </Button>
                                        </div>
                                        
                                        {/* GitHub Stats Display */}
                                        {githubStats && !githubStats.error && (
                                            <div className="mt-3 p-3 bg-black/20 rounded-sm border border-acid/20">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <img 
                                                        src={githubStats.avatar_url} 
                                                        alt={githubStats.username}
                                                        className="w-10 h-10 rounded-full border border-acid"
                                                    />
                                                    <div>
                                                        <p className="text-white font-medium text-sm">{githubStats.name || githubStats.username}</p>
                                                        <p className="text-acid text-xs">@{githubStats.username}</p>
                                                    </div>
                                                    <Badge className="ml-auto bg-acid/20 text-acid border-acid/40 text-xs">
                                                        Verified
                                                    </Badge>
                                                </div>
                                                <div className="grid grid-cols-4 gap-2 text-center">
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-acid font-bold text-lg">{githubStats.public_repos}</p>
                                                        <p className="text-muted-foreground text-xs">Repos</p>
                                                    </div>
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-acid font-bold text-lg">{githubStats.total_stars}</p>
                                                        <p className="text-muted-foreground text-xs flex items-center justify-center gap-1">
                                                            <Star className="w-3 h-3" /> Stars
                                                        </p>
                                                    </div>
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-acid font-bold text-lg">{githubStats.followers}</p>
                                                        <p className="text-muted-foreground text-xs">Followers</p>
                                                    </div>
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-acid font-bold text-lg">{githubStats.total_forks}</p>
                                                        <p className="text-muted-foreground text-xs flex items-center justify-center gap-1">
                                                            <GitFork className="w-3 h-3" /> Forks
                                                        </p>
                                                    </div>
                                                </div>
                                                {githubStats.top_languages?.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                        {githubStats.top_languages.map((lang, idx) => (
                                                            <Badge key={idx} variant="outline" className="text-xs border-neon-cyan/30 text-neon-cyan">
                                                                <Code className="w-3 h-3 mr-1" />
                                                                {lang.language}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Ethereum Wallet */}
                                    <div>
                                        <label className="block text-sm text-muted-foreground mb-2 font-mono uppercase tracking-wider flex items-center gap-2">
                                            <Wallet className="w-4 h-4" />
                                            Link Ethereum Wallet
                                        </label>
                                        <div className="flex gap-2">
                                            <Input
                                                value={profile.wallet_address}
                                                onChange={(e) => setProfile(prev => ({ ...prev, wallet_address: e.target.value }))}
                                                placeholder="0x..."
                                                className="bg-black/20 border-border focus:border-cyber-purple font-mono text-sm"
                                            />
                                            <Button
                                                onClick={handleLinkWallet}
                                                disabled={isLoadingWallet}
                                                variant="outline"
                                                className="border-cyber-purple text-cyber-purple hover:bg-cyber-purple/10"
                                            >
                                                {isLoadingWallet ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    'Link'
                                                )}
                                            </Button>
                                        </div>
                                        
                                        {/* Etherscan Stats Display */}
                                        {etherscanStats && !etherscanStats.error && (
                                            <div className="mt-3 p-3 bg-black/20 rounded-sm border border-cyber-purple/20">
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-white font-mono text-xs truncate flex-1">
                                                        {etherscanStats.address.slice(0, 6)}...{etherscanStats.address.slice(-4)}
                                                    </p>
                                                    <Badge className="bg-cyber-purple/20 text-cyber-purple border-cyber-purple/40 text-xs">
                                                        {etherscanStats.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2 text-center">
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-cyber-purple font-bold text-lg">{etherscanStats.eth_balance}</p>
                                                        <p className="text-muted-foreground text-xs">ETH</p>
                                                    </div>
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-cyber-purple font-bold text-lg">{etherscanStats.transaction_count}</p>
                                                        <p className="text-muted-foreground text-xs">Txns</p>
                                                    </div>
                                                    <div className="p-2 bg-void-subtle rounded-sm">
                                                        <p className="text-cyber-purple font-bold text-lg">{etherscanStats.deployed_contracts_count}</p>
                                                        <p className="text-muted-foreground text-xs">Contracts</p>
                                                    </div>
                                                </div>
                                                {etherscanStats.token_interactions?.length > 0 && (
                                                    <div className="mt-2">
                                                        <p className="text-muted-foreground text-xs mb-1">Token Interactions:</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {etherscanStats.token_interactions.slice(0, 5).map((token, idx) => (
                                                                <Badge key={idx} variant="outline" className="text-xs border-amber-400/30 text-amber-400">
                                                                    {token}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <a 
                                                    href={etherscanStats.etherscan_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-2 text-xs text-cyber-purple hover:underline flex items-center gap-1"
                                                >
                                                    View on Etherscan <ExternalLink className="w-3 h-3" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="w-full bg-acid text-black font-mono uppercase tracking-wider text-sm hover:bg-acid-hover"
                                    data-testid="save-profile-btn"
                                >
                                    {isSaving ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    )}
                                    Save Changes
                                </Button>
                            </CardContent>
                        </Card>

                        {/* CV History */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                                    CV History
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {cvs.length === 0 ? (
                                    <p className="text-muted-foreground text-sm text-center py-8">
                                        No CVs uploaded yet
                                    </p>
                                ) : (
                                    <ScrollArea className="h-[300px]">
                                        <div className="space-y-2">
                                            {cvs.map((cv) => (
                                                <div
                                                    key={cv.cv_id}
                                                    className="p-4 rounded-sm border border-border hover:border-acid/50 transition-colors group"
                                                    data-testid={`cv-history-${cv.cv_id}`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-white text-sm font-medium truncate">
                                                                {cv.filename}
                                                            </p>
                                                            <p className="text-muted-foreground text-xs font-mono mt-1">
                                                                {new Date(cv.uploaded_at).toLocaleDateString()} • {cv.skills?.length || 0} skills
                                                            </p>
                                                            {cv.skills?.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                    {cv.skills.slice(0, 5).map((skill, idx) => (
                                                                        <Badge
                                                                            key={idx}
                                                                            variant="outline"
                                                                            className="font-mono text-[10px] border-border text-muted-foreground"
                                                                        >
                                                                            {skill}
                                                                        </Badge>
                                                                    ))}
                                                                    {cv.skills.length > 5 && (
                                                                        <Badge variant="outline" className="font-mono text-[10px] border-border text-muted-foreground">
                                                                            +{cv.skills.length - 5}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteCv(cv.cv_id)}
                                                            className="text-muted-foreground hover:text-neon-red opacity-0 group-hover:opacity-100 transition-opacity"
                                                            data-testid={`delete-cv-${cv.cv_id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>

                        {/* Privacy Controls */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-neon-cyan" strokeWidth={1.5} />
                                    Privacy & Data
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Data Retention Policy */}
                                <div className="p-3 bg-black/20 rounded-sm border border-border">
                                    <h4 className="text-sm font-mono text-white mb-2">Data Retention Policy</h4>
                                    <ul className="text-xs text-muted-foreground space-y-1">
                                        <li>• Your CV data is stored securely and used only for job matching</li>
                                        <li>• We may share your profile with potential employers you apply to</li>
                                        <li>• Inactive accounts and data are deleted after 24 months</li>
                                        <li>• You can delete your data at any time using the button below</li>
                                    </ul>
                                </div>

                                {/* Delete Data Section */}
                                {!showDeleteConfirm ? (
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="w-full border-neon-red/30 text-neon-red hover:bg-neon-red/10 hover:border-neon-red font-mono uppercase tracking-wider text-sm"
                                        data-testid="delete-data-btn"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Delete All My Data
                                    </Button>
                                ) : (
                                    <div className="p-4 border border-neon-red/50 rounded-sm bg-neon-red/5">
                                        <div className="flex items-start gap-3 mb-4">
                                            <AlertTriangle className="w-5 h-5 text-neon-red flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                                            <div>
                                                <p className="text-white text-sm font-medium mb-1">Are you sure?</p>
                                                <p className="text-muted-foreground text-xs">
                                                    This will permanently delete your account, all CVs, saved jobs, and application history. This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowDeleteConfirm(false)}
                                                className="flex-1 border-border text-muted-foreground hover:text-white font-mono text-xs"
                                                disabled={isDeleting}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleDeleteAllData}
                                                disabled={isDeleting}
                                                className="flex-1 bg-neon-red text-white hover:bg-neon-red/80 font-mono text-xs"
                                                data-testid="confirm-delete-btn"
                                            >
                                                {isDeleting ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                ) : (
                                                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                                )}
                                                Yes, Delete Everything
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
}
